import { amazonAccountRegion } from '@/api/advertisingAccountRegions';
import type { FlipkartAdsAdAccount, FlipkartAdsOauthConnection } from '@/api/client';
import {
  accountLabel,
  extractAccountRows,
  type LinkedAccountRow,
} from '@/api/managerAccounts';

export type WorkspacePlatform = 'amazon' | 'flipkart' | 'brand';

export type AmazonWorkspaceScope = {
  platform: 'amazon';
  accountId: string;
};

export type FlipkartWorkspaceScope = {
  platform: 'flipkart';
  accountId: string;
  credentialId: string;
};

export type BrandWorkspaceScope = {
  platform: 'brand';
  accountId: string;
};

export type WorkspaceScope = AmazonWorkspaceScope | FlipkartWorkspaceScope | BrandWorkspaceScope;

export type WorkspaceAccountOption = WorkspaceScope & {
  label: string;
  /** Two-letter country/marketplace code for Amazon options only. */
  countryCode?: string | null;
};

export type WorkspaceNavTab = {
  label: string;
  to: string;
  end: boolean;
};

const FLIPKART_CAMPAIGN_TABS = new Set(['dashboard', 'workspace', 'report']);
const BRAND_CAMPAIGN_TABS = new Set(['dashboard']);

const AMAZON_CAMPAIGN_TABS = new Set([
  'dashboard',
  'overview',
  'workspace',
  'list',
  'analysis',
  'budget-rules',
  'automations',
  'reports',
  'compare',
  'recommendations',
  'daily-spend',
  'budget-usage',
  'stream',
  'chat',
  'agent',
  'sapyon-agent',
  'audience',
  'retail',
  'sov',
  'report',
]);

const FLIPKART_CAMPAIGN_PATH =
  /^\/flipkart\/([^/]+)\/accounts\/([^/]+)\/campaigns(?:\/|$)/;
const FLIPKART_LEGACY_CAMPAIGN_PATH = /^\/flipkart\/([^/]+)\/campaigns(?:\/|$)/;
const BRAND_CAMPAIGN_PATH = /^\/brands\/([^/]+)\/campaigns(?:\/|$)/;

export function isWorkspaceShellPath(pathname: string): boolean {
  return (
    /^\/accounts\/[^/]+\/campaigns(?:\/|$)/.test(pathname) ||
    FLIPKART_CAMPAIGN_PATH.test(pathname) ||
    FLIPKART_LEGACY_CAMPAIGN_PATH.test(pathname) ||
    BRAND_CAMPAIGN_PATH.test(pathname)
  );
}

export function parseWorkspaceScope(pathname: string): WorkspaceScope | null {
  const amazon = pathname.match(/^\/accounts\/([^/]+)\/campaigns(?:\/|$)/);
  if (amazon?.[1]) {
    return { platform: 'amazon', accountId: decodeURIComponent(amazon[1]) };
  }
  const flipkart = pathname.match(FLIPKART_CAMPAIGN_PATH);
  if (flipkart?.[1] && flipkart[2]) {
    return {
      platform: 'flipkart',
      credentialId: decodeURIComponent(flipkart[1]),
      accountId: decodeURIComponent(flipkart[2]),
    };
  }
  const brand = pathname.match(BRAND_CAMPAIGN_PATH);
  if (brand?.[1]) {
    return { platform: 'brand', accountId: decodeURIComponent(brand[1]) };
  }
  return null;
}

export function parseFlipkartLegacyCampaignPath(
  pathname: string
): { credentialId: string; tab: string } | null {
  if (FLIPKART_CAMPAIGN_PATH.test(pathname)) return null;
  const match = pathname.match(FLIPKART_LEGACY_CAMPAIGN_PATH);
  if (!match?.[1]) return null;
  const tab = campaignTabFromPathname(pathname);
  const allowed = allowedCampaignTabs('flipkart');
  return {
    credentialId: decodeURIComponent(match[1]),
    tab: tab && allowed.has(tab) ? tab : 'dashboard',
  };
}

export function workspaceCampaignBase(scope: WorkspaceScope): string {
  if (scope.platform === 'flipkart') {
    const credentialId = encodeURIComponent(scope.credentialId);
    const accountId = encodeURIComponent(scope.accountId);
    return `/flipkart/${credentialId}/accounts/${accountId}/campaigns`;
  }
  if (scope.platform === 'brand') {
    return `/brands/${encodeURIComponent(scope.accountId)}/campaigns`;
  }
  return `/accounts/${encodeURIComponent(scope.accountId)}/campaigns`;
}

export function workspaceCampaignPath(scope: WorkspaceScope, tab: string): string {
  return `${workspaceCampaignBase(scope)}/${tab}`;
}

export function campaignTabFromPathname(pathname: string): string | null {
  const match = pathname.match(/\/campaigns\/([^/]+)/);
  return match?.[1] ?? null;
}

export function allowedCampaignTabs(platform: WorkspacePlatform): Set<string> {
  if (platform === 'flipkart') return FLIPKART_CAMPAIGN_TABS;
  if (platform === 'brand') return BRAND_CAMPAIGN_TABS;
  return AMAZON_CAMPAIGN_TABS;
}

/** Preserve Dashboard/Workspace across platforms; Amazon-only tabs fall back on Flipkart. */
export function pathAfterWorkspaceAccountSwitch(
  next: WorkspaceScope,
  pathname: string
): string {
  const tab = campaignTabFromPathname(pathname);
  const allowed = allowedCampaignTabs(next.platform);
  const nextTab = tab && allowed.has(tab) ? tab : 'dashboard';
  return workspaceCampaignPath(next, nextTab);
}

export function encodeWorkspaceAccountValue(scope: WorkspaceScope): string {
  if (scope.platform === 'flipkart') {
    return `flipkart:${scope.credentialId}:${scope.accountId}`;
  }
  if (scope.platform === 'brand') {
    return `brand:${scope.accountId}`;
  }
  return `amazon:${scope.accountId}`;
}

export function decodeWorkspaceAccountValue(value: string): WorkspaceScope | null {
  const idx = value.indexOf(':');
  if (idx <= 0) return null;
  const platform = value.slice(0, idx);
  const rest = value.slice(idx + 1);
  if (!rest) return null;
  if (platform === 'amazon') {
    return { platform, accountId: rest };
  }
  if (platform === 'flipkart') {
    const sep = rest.indexOf(':');
    if (sep <= 0) return null;
    const credentialId = rest.slice(0, sep);
    const accountId = rest.slice(sep + 1);
    if (!credentialId || !accountId) return null;
    return { platform, credentialId, accountId };
  }
  if (platform === 'brand') {
    return { platform, accountId: rest };
  }
  return null;
}

export function sameWorkspaceAccount(left: WorkspaceScope, right: WorkspaceScope): boolean {
  if (left.platform !== right.platform || left.accountId !== right.accountId) return false;
  if (left.platform === 'flipkart' && right.platform === 'flipkart') {
    return left.credentialId === right.credentialId;
  }
  return true;
}

export function flipkartConnectionLabel(
  connection: Pick<FlipkartAdsOauthConnection, 'display_name' | 'advertiser_id'>
): string {
  return connection.display_name || connection.advertiser_id || 'Flipkart Ads connection';
}

export function flipkartAdAccountLabel(
  account: Pick<FlipkartAdsAdAccount, 'name' | 'ad_account_id'>
): string {
  const name = account.name?.trim();
  return name || account.ad_account_id;
}

export function selectableAmazonAccounts(rows: LinkedAccountRow[]): WorkspaceAccountOption[] {
  const seen = new Set<string>();
  const out: WorkspaceAccountOption[] = [];
  for (const row of rows) {
    const profileId = row.profileId;
    if (!profileId || seen.has(profileId)) continue;
    seen.add(profileId);
    out.push({
      platform: 'amazon',
      accountId: profileId,
      label: accountLabel(row),
      countryCode: amazonAccountRegion(row),
    });
  }
  out.sort((a, b) => a.label.localeCompare(b.label));
  return out;
}

export function selectableAmazonAccountsFromProfiles(data: unknown): WorkspaceAccountOption[] {
  return selectableAmazonAccounts(extractAccountRows(data));
}

export function selectableFlipkartAccounts(
  accounts: FlipkartAdsAdAccount[]
): WorkspaceAccountOption[] {
  const out = accounts.map((account) => ({
    platform: 'flipkart' as const,
    accountId: account.ad_account_id,
    credentialId: account.credential_id,
    label: flipkartAdAccountLabel(account),
  }));
  out.sort((a, b) => a.label.localeCompare(b.label));
  return out;
}

export function selectableBrandAccounts(
  brands: Array<{ id: string; name: string }>
): WorkspaceAccountOption[] {
  const out = brands
    .filter((brand) => brand.id && brand.name.trim())
    .map((brand) => ({
      platform: 'brand' as const,
      accountId: brand.id,
      label: brand.name.trim(),
    }));
  out.sort((a, b) => a.label.localeCompare(b.label));
  return out;
}

const AMAZON_NAV_TABS: Array<{ label: string; tab: string }> = [
    { label: 'Dashboard', tab: 'dashboard' },
    { label: 'Workspace', tab: 'workspace' },
    // { label: 'Budget rules', tab: 'budget-rules' },
    { label: 'Automations', tab: 'automations' },
    { label: 'Recommendations', tab: 'recommendations' },
    { label: 'Daily spend', tab: 'daily-spend' },
    { label: 'Dayparting', tab: 'stream' },
    // { label: 'Dayparting v2', tab: 'stream-v2' },
    { label: 'Campaigns', tab: 'list' },
    { label: 'Report', tab: 'report' },
    { label: 'Chat', tab: 'chat' },
    { label: 'Audience', tab: 'audience' },
    { label: 'Analysis', tab: 'analysis' },
    { label: 'N-word', tab: 'n-words' },
    { label: 'Share of voice', tab: 'sov' },
    { label: 'Vendor', tab: 'retail' },
  ];

const FLIPKART_NAV_TABS: Array<{ label: string; tab: string }> = [
  { label: 'Dashboard', tab: 'dashboard' },
  { label: 'Workspace', tab: 'workspace' },
  { label: 'Report', tab: 'report' },
];

const BRAND_NAV_TABS: Array<{ label: string; tab: string }> = [
  { label: 'Dashboard', tab: 'dashboard' },
];

const FALLBACK_NAV_TABS: WorkspaceNavTab[] = [
  { label: 'Dashboard', to: '/dashboard', end: true },
  { label: 'Budget rules', to: '/accounts', end: true },
  { label: 'Recommendations', to: '/accounts', end: true },
  { label: 'Dayparting', to: '/accounts', end: true },
  { label: 'Chat', to: '/accounts', end: true },
  { label: 'Audience', to: '/accounts', end: true },
];

export function buildWorkspaceNavTabs(pathname: string): WorkspaceNavTab[] {
  const scope = parseWorkspaceScope(pathname);
  if (!scope) return FALLBACK_NAV_TABS;
  const base = workspaceCampaignBase(scope);
  const items =
    scope.platform === 'flipkart'
      ? FLIPKART_NAV_TABS
      : scope.platform === 'brand'
        ? BRAND_NAV_TABS
        : AMAZON_NAV_TABS;
  return items.map(({ label, tab }) => ({
    label,
    to: `${base}/${tab}`,
    end: true,
  }));
}
